import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. This page ONLY consumes the #session_id fragment
// left in the URL by Emergent Auth, exchanges it with the backend, and routes
// the user to their next step.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const routeAfterLogin = (user, navigate) => {
  if (!user.consent_accepted) return navigate('/consent', { replace: true });
  if (!user.current_phase) return navigate('/assessment', { replace: true });
  if (!['active', 'trialing'].includes(user.subscription_status)) {
    return navigate('/checkout', { replace: true });
  }
  return navigate('/sanctuary', { replace: true });
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  // useRef so StrictMode's double-invoke can't fire two exchanges
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      toast.error('Google sign-in link is missing or expired');
      navigate('/login', { replace: true });
      return;
    }

    const exchange = async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        const { access_token, user } = res.data;
        login(access_token, user);
        // Clean the fragment so a refresh doesn't try to re-exchange it
        window.history.replaceState(null, '', window.location.pathname);
        toast.success(`Welcome, ${user.preferred_name || user.name || 'friend'}`);
        routeAfterLogin(user, navigate);
      } catch (error) {
        logError('Google session exchange failed', error);
        const detail = error?.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Google sign-in failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    exchange();
  }, [navigate, login]);

  return (
    <div
      className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6"
      data-testid="auth-callback-page"
    >
      <div className="text-center">
        <div
          className="mx-auto mb-6 h-10 w-10 animate-spin rounded-full border-2 border-stone-300 border-t-stone-700"
          aria-hidden="true"
        />
        <p
          className="text-stone-700 text-lg"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Signing you in gently…
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
